import { CHARACTERS, type PlayerMods } from './content.ts';
import { addMods } from './rules.ts';
import { createSeededRandom } from '../utils/random.ts';

export type MutatorId =
  | 'glass_cannon'
  | 'hypercharge'
  | 'volatile_horde'
  | 'blood_bounty'
  | 'swarm_surge';

export interface MutatorDef {
  id: MutatorId;
  color: string;
  mods: Partial<PlayerMods>;
  enemySpeedMult: number;
  spawnRateMult: number;
  healMult: number;
  volatileDeath: boolean;
}

export const DAILY_MUTATORS: readonly MutatorDef[] = [
  {
    id: 'glass_cannon',
    color: '#ff6b81',
    mods: { might: 0.6, maxHp: -35 },
    enemySpeedMult: 1,
    spawnRateMult: 1,
    healMult: 1,
    volatileDeath: false,
  },
  {
    id: 'hypercharge',
    color: '#5fc8ff',
    mods: { haste: 0.3 },
    enemySpeedMult: 1.2,
    spawnRateMult: 1,
    healMult: 1,
    volatileDeath: false,
  },
  {
    id: 'volatile_horde',
    color: '#ff9a3c',
    mods: { area: 0.2 },
    enemySpeedMult: 1,
    spawnRateMult: 1,
    healMult: 1,
    volatileDeath: true,
  },
  {
    id: 'blood_bounty',
    color: '#ffd24d',
    mods: { greed: 0.75, crit: 0.1 },
    enemySpeedMult: 1,
    spawnRateMult: 1,
    healMult: 0.5,
    volatileDeath: false,
  },
  {
    id: 'swarm_surge',
    color: '#8eff5c',
    mods: { growth: 0.35, magnet: 0.35 },
    enemySpeedMult: 1,
    spawnRateMult: 1.35,
    healMult: 1,
    volatileDeath: false,
  },
];

export const DAILY_BOUNTY_GOLD = 250;

// The Daily draws from the first six realms only: drawing from STAGES.length would move every Daily (today's
// included) each time a realm is added. server/app.mjs dailyStage uses the same six.
export const DAILY_STAGE_COUNT = 6;

export interface DailyTrialSpec {
  date: string;
  seed: number;
  characterId: string;
  stageIndex: number;
  stormTier: number;
  mutators: [MutatorId, MutatorId];
}

function hashDateSeed(dateStr: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < dateStr.length; i += 1) {
    h ^= dateStr.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return (h || 0x9e3779b9) >>> 0;
}

export function todayUtcString(): string {
  return new Date().toISOString().slice(0, 10);
}

export function getDailyTrial(dateStr = todayUtcString()): DailyTrialSpec {
  const seed = hashDateSeed(`daily:${dateStr}`);
  const rng = createSeededRandom(seed);
  const charIdx = Math.floor(rng() * CHARACTERS.length);
  const stageIdx = Math.floor(rng() * DAILY_STAGE_COUNT);
  const pool = DAILY_MUTATORS.map((m) => m.id);
  const firstIdx = Math.floor(rng() * pool.length);
  const first = pool.splice(firstIdx, 1)[0];
  const second = pool[Math.floor(rng() * pool.length)];
  return {
    date: dateStr,
    seed,
    characterId: CHARACTERS[charIdx].id,
    stageIndex: stageIdx,
    stormTier: 2,
    mutators: [first, second],
  };
}

export function mutatorById(id: MutatorId): MutatorDef {
  return DAILY_MUTATORS.find((m) => m.id === id) ?? DAILY_MUTATORS[0];
}

export function applyDailyMutators(target: PlayerMods, mutatorIds: ReadonlyArray<MutatorId>): PlayerMods {
  for (const id of mutatorIds) {
    const def = mutatorById(id);
    addMods(target, def.mods);
  }
  return target;
}
